import { z } from 'zod';
import { SectionSchemaZod } from './section.schema.zod';
import { TabSchemaZod } from './tab.schema.zod';
import { ValidationSchemaZod } from './validation.schema.zod';
import { RuleSchemaZod } from './rule.schema.zod';
import { ActionSchemaZod } from './action.schema.zod';
import { HeaderSchemaZod } from './header.schema.zod';
import { ActionsSlotSchemaZod } from './actions.slot.schema.zod';
import { SurfaceType } from './surface.type';
import { SectionColumnSchemaZod } from './section.column.schema.zod';

export type SurfaceSchemaIdentifier = 'surface-engine/v1';

export const SurfaceCreateMoreSchemaZod = z.object({ label: z.string().min(1).optional(), clear: z.array(z.string().min(1)).min(1), focus: z.string().min(1) }).strict();

export type SurfaceCreateMoreSchema = z.infer<typeof SurfaceCreateMoreSchemaZod>;

export const SurfaceSchemaZod = z.object({
  $schema: z.literal('surface-engine/v1'),
  version: z.string().regex(/^\d+\.\d+\.\d+(?:-[0-9A-Za-z.-]+)?(?:\+[0-9A-Za-z.-]+)?$/, 'Surface version must use semantic version format (for example, 1.0.0).'),
  surface: z.nativeEnum(SurfaceType),
  entity: z.string().optional(),
  header: HeaderSchemaZod.optional(),
  summary: z.array(SectionSchemaZod).optional(),
  tabs: z.array(TabSchemaZod).optional(),
  sections: z.array(SectionSchemaZod).optional(),
  // Named section bags for multi-slot surfaces (financialDocument: meta/parties/…).
  // Keys match surface.slots names; paths compile as `{slotName}.{sectionId}`.
  namedSlots: z.record(z.string().min(1), z.array(SectionSchemaZod)).optional(),
  // Explicit independently stacked columns for section slots. Keys are slot
  // names such as `body`; flat section arrays remain the auto-layout form.
  sectionColumns: z.record(z.string().min(1), z.array(SectionColumnSchemaZod).min(1)).optional(),
  // Ordered header command list for surfaces with an actions slot.
  actionsSlot: ActionsSlotSchemaZod.optional(),
  // Form-host primary footer label. Omit for "Save". Field-group picker dialogs use "Done" — they apply to the parent document, they do not persist.
  submitLabel: z.string().min(1).optional(),
  // Form-host secondary footer label. Omit for "Cancel"; a later step of a multi-step dialog reads "Back".
  cancelLabel: z.string().min(1).optional(),
  // Primary label while submit runs. Omit to keep the label (with the spinner); a card charge reads "Processing…".
  submittingLabel: z.string().min(1).optional(),
  // Optional repeated-create footer. Form-only; clear/focus binds are compiler-validated.
  createMore: SurfaceCreateMoreSchemaZod.optional(),
  validations: z.array(ValidationSchemaZod).optional(),
  rules: z.array(RuleSchemaZod).optional(),
  actions: z.array(ActionSchemaZod).optional(),
}).strict();

export type SurfaceSchema = z.infer<typeof SurfaceSchemaZod>;
